import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { helper } from 'src/environments/helper';
import { SigningLogsService } from './signing-logs.service';
import { SigningLogItem } from './signing-logs.interface';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-signing-logs',
    templateUrl: './signing-logs.component.html',
    styleUrls: ['./signing-logs.component.scss'],
})
export class SigningLogsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    showCard = false;
    isExporting = false;

    // Filters
    dateFrom: string = '';
    dateTo: string = '';
    statusFilter: string = '';
    certificateFilter: string = '';
    searchFilter: string = '';
    userIdFilter: string = '';
    documentIdFilter: string = '';
    documentRegistraturaIdFilter: string = '';

    // Dropdown options
    statuses: {value: string, label: string}[] = [];

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private signingLogsService: SigningLogsService
    ) {}

    ngOnInit(): void {
        this.loadStatuses();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    loadStatuses(): void {
        this.signingLogsService.getStatuses().subscribe({
            next: (response) => {
                if (response.statuses) {
                    this.statuses = response.statuses;
                }
            },
            error: (err) => {
                console.error('Error loading statuses:', err);
            }
        });
    }

    renderDT(): void {
        const self = this;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                const params: any = {
                    draw: dataTablesParameters.draw,
                    start: dataTablesParameters.start,
                    length: dataTablesParameters.length,
                };

                // Sorting
                if (dataTablesParameters.order && dataTablesParameters.order.length > 0) {
                    const orderCol = dataTablesParameters.order[0].column;
                    const orderDir = dataTablesParameters.order[0].dir;
                    const columns = ['id', 'document_id', 'document_registratura_id', 'original_filename', 'certificate_slug', 'status', 'user_name', 'client_ip', 'created_at'];
                    params.sort_by = columns[orderCol] || 'id';
                    params.sort_dir = orderDir;
                }

                // Filters
                if (self.dateFrom) params.date_from = self.dateFrom;
                if (self.dateTo) params.date_to = self.dateTo;
                if (self.statusFilter) params.status = self.statusFilter;
                if (self.certificateFilter) params.certificate_slug = self.certificateFilter;
                if (self.searchFilter) params.search = self.searchFilter;
                if (self.userIdFilter) params.user_id = self.userIdFilter;
                if (self.documentIdFilter) params.document_id = self.documentIdFilter;
                if (self.documentRegistraturaIdFilter) params.document_registratura_id = self.documentRegistraturaIdFilter;

                self.signingLogsService.getSigningLogs(params).subscribe({
                    next: (resp: any) => {
                        self.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal || 0,
                            recordsFiltered: resp.recordsFiltered || 0,
                            data: resp.data || [],
                        });
                    },
                    error: (err) => {
                        self.isLoading = false;
                        console.error('Error fetching signing logs:', err);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: [],
                        });
                    }
                });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '60px',
                },
                {
                    title: 'Doc ID',
                    data: 'document_id',
                    width: '80px',
                    render: (data: number | null) => {
                        return data ? `<span class="badge bg-info">${data}</span>` : '-';
                    },
                },
                {
                    title: 'Doc Generat ID',
                    data: 'document_registratura_id',
                    width: '110px',
                    render: (data: number | null) => {
                        return data ? `<span class="badge bg-secondary">${data}</span>` : '-';
                    },
                },
                {
                    title: 'Fisier',
                    data: 'original_filename',
                    render: (data: string) => {
                        if (!data) return '-';
                        if (data.length > 25) {
                            return `<span title="${data}">${data.substring(0, 25)}...</span>`;
                        }
                        return data;
                    },
                },
                {
                    title: 'Certificat',
                    data: 'certificate_slug',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Status',
                    data: 'status',
                    render: (data: string) => {
                        const statusColors: Record<string, string> = {
                            'completed': 'success',
                            'signed': 'success',
                            'pending': 'warning',
                            'processing': 'info',
                            'failed': 'danger',
                            'error': 'danger',
                        };
                        const color = statusColors[data?.toLowerCase()] || 'secondary';
                        return `<span class="badge bg-${color}">${data || '-'}</span>`;
                    },
                },
                {
                    title: 'User',
                    data: 'user_name',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'IP',
                    data: 'client_ip',
                    orderable: false,
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Data',
                    data: 'created_at',
                    render: (data: string) => {
                        if (!data) return '-';
                        try {
                            const date = new Date(data);
                            return date.toLocaleString('ro-RO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            });
                        } catch {
                            return data;
                        }
                    },
                },
                {
                    title: 'Actiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        const isCompleted = row.status?.toLowerCase() === 'completed';
                        const downloadBtn = isCompleted ? `
                            <button class="action-btn download ms-1" title="Descarca fisierul semnat" data-request-id="${row.request_id}">
                                <i class="fa-solid fa-download"></i>
                            </button>
                        ` : '';
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button class="action-btn view ms-1" title="Vizualizeaza detalii" data-id="${row.id}">
                                <i class="fa-solid fa-eye"></i>
                            </button>
                            ${downloadBtn}
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: SigningLogItem) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewBtn = lastTd?.querySelector('.view') as HTMLElement;
                const downloadBtn = lastTd?.querySelector('.download') as HTMLElement;

                if (viewBtn) {
                    viewBtn.addEventListener('click', () => {
                        this.viewDetails(data);
                    });
                }

                if (downloadBtn) {
                    downloadBtn.addEventListener('click', () => {
                        this.downloadFile(data.request_id, data.original_filename);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        if (this.dtElement && this.dtElement.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.draw(paginate);
            });
        }
        this.showCard = false;
    }

    toggleCard(): void {
        this.showCard = !this.showCard;
    }

    resetForm(): void {
        this.dateFrom = '';
        this.dateTo = '';
        this.statusFilter = '';
        this.certificateFilter = '';
        this.searchFilter = '';
        this.userIdFilter = '';
        this.documentIdFilter = '';
        this.documentRegistraturaIdFilter = '';
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    viewDetails(data: SigningLogItem): void {
        this.signingLogsService.getSigningLogDetail(data.id).subscribe({
            next: (response: any) => {
                const detail = response.data || response;
                const statusColors: Record<string, string> = {
                    'completed': '#28a745',
                    'signed': '#28a745',
                    'pending': '#ffc107',
                    'processing': '#17a2b8',
                    'failed': '#dc3545',
                    'error': '#dc3545',
                    'expired': '#6c757d',
                    'deleted': '#6c757d',
                };
                const statusColor = statusColors[detail.status?.toLowerCase()] || '#6c757d';

                const formatDate = (dateStr: string) => {
                    if (!dateStr) return '-';
                    try {
                        return new Date(dateStr).toLocaleString('ro-RO', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit'
                        });
                    } catch { return dateStr; }
                };

                const formatBytes = (bytes: number) => {
                    if (!bytes) return '-';
                    if (bytes < 1024) return bytes + ' B';
                    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
                    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                };

                const htmlContent = `
                    <div style="text-align: left; font-size: 14px;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; width: 140px;">ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Request ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-family: monospace; font-size: 12px;">${detail.request_id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Status:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <span style="background: ${statusColor}; color: white; padding: 3px 10px; border-radius: 4px; font-size: 12px;">
                                        ${detail.status || '-'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Fisier original:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.original_filename || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Dimensiune fisier:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatBytes(detail.file_size_bytes)}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Certificat:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.certificate_slug || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Utilizator:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.user_name || '-'} ${detail.user_id ? '(ID: ' + detail.user_id + ')' : ''}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">IP Client:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.client_ip || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Creat la:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(detail.created_at)}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Semnat la:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(detail.signed_at)}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Expira la:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(detail.expires_at)}</td>
                            </tr>
                            ${detail.document_id ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Document ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_id}</td>
                            </tr>
                            ` : ''}
                            ${detail.document_registratura_id ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Registratura ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_registratura_id}</td>
                            </tr>
                            ` : ''}
                            ${detail.error_message ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; color: #dc3545;">Eroare:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; color: #dc3545;">${detail.error_message}</td>
                            </tr>
                            ` : ''}
                            ${detail.sts_sign_id ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">STS Sign ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-family: monospace; font-size: 12px;">${detail.sts_sign_id}</td>
                            </tr>
                            ` : ''}
                        </table>
                    </div>
                `;

                Swal.fire({
                    title: '<i class="fa-solid fa-file-signature"></i> Detalii Signing Log',
                    html: htmlContent,
                    width: 600,
                    confirmButtonText: 'Inchide',
                    confirmButtonColor: '#3085d6',
                });
            },
            error: (err: any) => {
                console.error('Error fetching log detail:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-au putut incarca detaliile.',
                    icon: 'error',
                    confirmButtonText: 'Inchide',
                });
            }
        });
    }

    exportCsv(): void {
        this.isExporting = true;
        const params: any = {};
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.certificateFilter) params.certificate_slug = this.certificateFilter;
        if (this.searchFilter) params.search = this.searchFilter;
        if (this.userIdFilter) params.user_id = this.userIdFilter;
        if (this.documentIdFilter) params.document_id = this.documentIdFilter;
        if (this.documentRegistraturaIdFilter) params.document_registratura_id = this.documentRegistraturaIdFilter;

        this.signingLogsService.exportCsv(params).subscribe({
            next: (blob) => {
                this.isExporting = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `signing-logs-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isExporting = false;
                console.error('Error exporting CSV:', err);
            }
        });
    }

    downloadFile(requestId: string, originalFilename: string | null): void {
        this.signingLogsService.downloadSignedFile(requestId).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // Use original filename or fallback to request_id
                const filename = originalFilename
                    ? `signed_${originalFilename}`
                    : `signed_${requestId}.pdf`;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                console.error('Error downloading file:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-a putut descarca fisierul. Este posibil sa fi expirat.',
                    icon: 'error',
                    confirmButtonText: 'Inchide',
                });
            }
        });
    }
}
